import React, { useEffect, useMemo } from 'react'
import {Droppable} from 'react-beautiful-dnd'
import {connect} from 'react-redux'
import Field from './Field'
import {updateFieldsState, addHiddenField, removeHiddenField } from '../action_creators'
import conditionGroupValidation from './utils'

const sToP = (state, op) => ({
  dragging: state.active.dragging,
  field: state.fields[op.fieldId],
  fields: state.fields,
})

const getStyle = (isDraggingOver) => {
  return isDraggingOver
    ? {
        border: '0.2rem dashed lightblue',
        borderRadius: '0.3rem',
      }
    : {}
}

const dToP = (dispatch) => ({
  updateFieldsState: (value) => dispatch(updateFieldsState(value)),
  addHiddenField: (value) => dispatch(addHiddenField(value)),
  removeHiddenField: (value) => dispatch(removeHiddenField(value))
})

const baseStyle = {border: '0.1rem solid lightgrey', zIndex: 10, marginBottom: '5px', minHeight: '150px',borderRadius:'8px'}

export function DnDWrapper({fieldId, dragging, field, fields, updateFieldsState, addHiddenField, removeHiddenField }) {

  const getWrapperStyle = useMemo(() => (snapshot) => 
    dragging ? {...baseStyle, ...getStyle(snapshot.isDraggingOver)} : baseStyle
  , [dragging]);

  // 1. CALCULATE VISIBILITY (Must happen before render)
  // We use useMemo so this complex logic doesn't run on every single re-render, 
  // but we need the result immediately to decide whether to return JSX or null.
  const visibilityData = useMemo(() => {
    const isParentDisplay = (questionID) => {
      if (fields[questionID]?.dependentQue?.selectedQuestion) {
        const {selectedQuestion, selectedOption} = fields[questionID].dependentQue
        if (selectedQuestion && selectedOption) {
          const values = fields[selectedQuestion]?.value?.values
          return values && values[selectedOption] ? true : false
        }
      }
      return true
    }

    const { cgList, operand } = field?.conditionGroup || {cgList: [], operand: "" }
    
    // Default state: Visible
    let result = { isVisible: true, cleanupTarget: null };

    if(cgList && cgList.length > 0) {
      result.isVisible = false; // Default to hidden if conditions exist
      
      const data = cgList[0].conditionList?.[0]
      const {selectedQuestion, selectedOption} = data || {selectedQuestion : '', selectedOption: ''}

      if (selectedQuestion && selectedOption) {
        if (fields && conditionGroupValidation(cgList, fields, operand)) {
          if (isParentDisplay(selectedQuestion) === undefined || isParentDisplay(selectedQuestion)) {
            result.isVisible = true;
          } else {
            // Logic: Hide and mark parent for cleanup 
            result.isVisible = false;
            result.cleanupTarget = selectedQuestion;
          }
        } else {
            // Logic: Hide and mark self for cleanup
            result.isVisible = false;
            result.cleanupTarget = fieldId;
        }
      }
    }
    return result;
  }, [fields, field, fieldId]); 

  // 2. HANDLE SIDE EFFECTS (Dispatching actions)
  // This runs after render. If visibility changed, we tell Redux here.
  useEffect(() => {
    const { isVisible, cleanupTarget } = visibilityData;

    if (!isVisible) {
      addHiddenField(fieldId);
      
      // FIX: Check if we actually need to cleanup to avoid infinite loops
      // and COPY the state before deleting.
      if (cleanupTarget && fields[cleanupTarget]?.value) {
        const fieldsCopy = { ...fields }; // Shallow copy
        if (fieldsCopy[cleanupTarget]) {
           // We need to clone the specific field object too if we are being strictly immutable,
           // but creating a new 'fields' object reference is usually enough for Redux to detect change.
           fieldsCopy[cleanupTarget] = { ...fieldsCopy[cleanupTarget] };
           delete fieldsCopy[cleanupTarget].value;
           
           updateFieldsState(fieldsCopy);
        }
      }
    } else {
      removeHiddenField(fieldId);
    }
  }, [visibilityData, fieldId, addHiddenField, removeHiddenField, updateFieldsState]); 
  // Removed 'fields' from dependency array to prevent infinite loops, 
  // relying on 'visibilityData' (which depends on fields) to trigger updates.


  // 3. CONDITIONAL RENDER
  // If it's hidden, we return null immediately.
  if (!visibilityData.isVisible) {
      return null; 
  }

  return (
    <div>
      {fieldId && (
        <Droppable droppableId={fieldId}>
          {(provided, snapshot) => (
            <div className="flex-row padding-2" style={getWrapperStyle(snapshot)} ref={provided.innerRef}>
              <Field {...{fieldId, provided, snapshot}} />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
    </div>
  )
}

const WrappedDnDWrapper = connect(sToP, dToP)(DnDWrapper)
export default WrappedDnDWrapper